/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class ComplexFilterProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.filterState = {};
		this.blacklistedFilters = [ 'DeleteDate', 'ExternalSyncDate', 'ExternalSyncGUID', 'UpdatingIDUser', 'UpdateDate' ];
		this.renderOverriden = false;
		// NOTE: endCapTemplate is kept as a public option but is no longer rendered — its only
		// consumer was the old PRSP-SUBSET-Filters-Template-Input-Fieldset (removed). The module owns
		// the search row now; reintroduce an end-cap slot there if an app turns out to need one.
		this.endCapTemplate = pOptions.endCapTemplate || '';
		// a local copy of the current route params to avoid "ComplexFilterView" view being set to pViewContext at any given time
		this.routeViewHash = null;
		this.routeRecordSet = null;
	}
	/*
		KNOWN TODO/BUGS:
		- Wrong input types generating for filter descriptors ("DateRange" is generating a "StringMatch" input, etc) -> This is a lib bug (added hack/workaround for now).
		- Add "complex filters" like "IDUser > (User Name First + User Name Last)", etc
		- Basic search query doesn't persist across views when doing a search (core library/route issue)
	*/

	/**
	 * Initializes the filter experience manager for the LIMS app by extracting the record set and view context from the current route, opening the filter persistence UI, and setting callbacks to update the filter experience view when filters are added, updated, or deleted.
	 * This ensures that the filter experience view is always ready to go when the user opens it, and that it stays in sync with the current filters and any changes made to them.
	 */
	initializeFilterExperienceManagerForLIMSApp()
	{
		const tmpRouteUrl = this.pict.providers?.RecordSetRouter?.pictRouter?.router?.current?.[0]?.url;
		this.routeRecordSet = tmpRouteUrl?.split?.('PSRS/')?.[1]?.split?.('/')?.[0];
		this.routeViewHash = tmpRouteUrl?.split?.('PSRS/')?.[1]?.split?.('/')?.[1];
		// always initiailize the filter experience view so that it's ready to go when the user opens it, but keep it hidden until then
		this.pict.views.FilterPersistenceView.initializeFilterPersistenceViewUI(this.routeRecordSet, this.routeViewHash, () =>
		{
			this.inintializeSelect2('#FilterPersistenceView-StoredFiltersSelect');
			// set the callback to reset the filter experience view when adding a new filter to the filter list
			this.pict.views['PRSP-Filters'].setAddFilterCallback(() =>
			{
				this.pict.providers.FilterDataProvider.filterExperienceModifiedFromURLHash = true;
				this.pict.views.FilterPersistenceView.handleModifiedFiltersState();
			});
			// set the callback to reset the filter experience view when updating/deleting a filter in the filter list
			this.pict.views['PRSP-Filters'].setRemoveFilterCallback(() =>
			{
				this.pict.providers.FilterDataProvider.filterExperienceModifiedFromURLHash = true;
				this.pict.views.FilterPersistenceView.handleModifiedFiltersState();
			});
		});
	}

	/**
	 * Initializes the select2 dropdown for saved filter experiences.
	 * This is called when the user opens the filter experience section for the first time, and also whenever the filter experience data is updated to ensure the dropdown is in sync.
	 * @param {string} pHTMLElementSelector - The selector for the HTML select element to initialize select2 on.
	 */
	inintializeSelect2(pHTMLElementSelector)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpSelectElementSelector = document.querySelector(pHTMLElementSelector);
			// get all the options with values on the select element and use them to populate the select2 dropdown, this is needed to properly display the saved filter experiences in the filter experience dropdown
			const tmpOptions = [];
			if (tmpSelectElementSelector && tmpSelectElementSelector instanceof HTMLSelectElement)
			{
				for (const option of tmpSelectElementSelector.options)
				{
					if (option.value)
					{
						tmpOptions.push({ id: option.value, text: option.text });
					}
				}
			}
			const tmpSelectOptions = {
				data: tmpOptions,
				width: '100%',
				placeholder: 'Select a filter experience',
				dropdownParent: document.querySelector('#FilterPersistenceView-Container'),
			};
			// if the select2 plugin is not already initialized, initialize it now
			if (!window['jQuery'](tmpSelectElementSelector).data('select2'))
			{
				window['jQuery'](tmpSelectElementSelector).select2(tmpSelectOptions);
			}
			else
			{
				// if it is already initialized, we need to update the options manually and trigger a change to update the display
				window['jQuery'](tmpSelectElementSelector).empty().select2(tmpSelectOptions);
				window['jQuery'](tmpSelectElementSelector).trigger('change');
			}
		}
	}

	handleSaveFilterPersistenceSettings(event, recordSet, viewContext)
	{
		const tmpCurrentFilterName = this.pict.providers.FilterDataProvider.getCurrentFilterName();
		this.pict.views.FilterPersistenceView.saveFilterPersistenceSettings(event, () =>
		{
			this.pict.views.HeadlightPanels.toast({
				message: `Filter experience "${tmpCurrentFilterName}" saved successfully`,
				type: 'is-success',
			});
		});
	}

	handleDeleteFilterPersistenceSettings(event)
	{
		const tmpCurrentFilterName = this.pict.providers.FilterDataProvider.getCurrentFilterName();
		this.pict.views.FilterPersistenceView.deleteFilterPersistenceSettings(event, () =>
		{
			this.pict.views.HeadlightPanels.toast({
				message: `Filter experience "${tmpCurrentFilterName}" deleted successfully`,
				type: 'is-success',
			});
		});
	}


	getActiveFilters()
	{
		const activeRecordSet = this.pict.providers?.RecordSetRouter?.pictRouter?.router?.current?.[0]?.data?.RecordSet || '';
		const activeFilters = this.pict?.Bundle?._ActiveFilterState?.[activeRecordSet]?.FilterClauses || [];
		//FIXME: don't want to make everything cascading async but want to force load this if it isn't there
		this.pict.providers?.[`RSP-Provider-${ activeRecordSet }`]?.getRecordSchema?.()?.catch?.((e) =>
		{
			this.pict.log.error('Failed to get record schema for active record set', e);
		});
		const activeSchema = this.pict.providers?.[`RSP-Provider-${ activeRecordSet }`]?.getFilterSchema();
		return { activeFilters, activeRecordSet, activeSchema };
	}

	updateFilters()
	{
		this.filterState = this.getActiveFilters();
	}


	/**
	 * Prepares the complex filter view by overriding the onBeforeRender and onAfterRender functions of the filter view to update the filter state and apply visibility states for disabled filters.
	 * Also overrides the prepareRecord function for specific filter types to ensure the correct input types are generated for the filter descriptors (workaround for lib bug).
	 * This is called when the filter view is rendered to ensure the complex filter functionality is properly integrated into the filter view.
	 */
	prepareComplexFilter()
	{
		if (!this.renderOverriden)
		{
			const beforeRenderStub = this.pict.views['PRSP-Filters'].onBeforeRender.bind(this.pict.views['PRSP-Filters']); ;
			this.pict.views['PRSP-Filters'].onBeforeRender =
			function(callback, renderable)
			{
				this.pict.providers.ComplexFilterProvider.prepareComplexFilter();
				beforeRenderStub(callback, renderable);
			};
			this.pict.views['PRSP-Filters'].onBeforeRender = this.pict.views['PRSP-Filters'].onBeforeRender.bind(this.pict.views['PRSP-Filters']);
			const afterRenderStub = this.pict.views['PRSP-Filters'].onAfterRender.bind(this.pict.views['PRSP-Filters']); ;
			this.pict.views['PRSP-Filters'].onAfterRender =
			function(callback, renderable)
			{
				this.pict.providers.ComplexFilterProvider.initializeFilterExperienceManagerForLIMSApp();
				afterRenderStub(callback, renderable);
			};
			this.pict.views['PRSP-Filters'].onAfterRender = this.pict.views['PRSP-Filters'].onAfterRender.bind(this.pict.views['PRSP-Filters']);
			// Entity-reference filters now delegate their input to the pict-native 'Picker' InputType
			// (the select2-free replacement for EntitySelectorMultiple). pict-section-recordset's Phase 3
			// entity-reference base natively stamps the ClauseDescriptor InputType + Entity and bridges
			// Values↔StringArrayValue, so the prepareRecord patches that used to live here are gone — we
			// just opt the filter types into the picker.
			[ 'InternalJoinSelectedValue', 'InternalJoinSelectedValueList', 'ExternalJoinSelectedValue', 'ExternalJoinSelectedValueList' ].forEach((pType) =>
			{
				const tmpEntityFilterView = this.pict.views['PRSP-FilterType-' + pType];
				if (tmpEntityFilterView) { tmpEntityFilterView.options.EntityInputType = 'Picker'; }
			});
			const tempPrepareStringRecord = this.pict.views['PRSP-FilterType-StringMatch'].prepareRecord.bind(this.pict.views['PRSP-FilterType-StringMatch']);
			this.pict.views['PRSP-FilterType-StringMatch'].prepareRecord =
			function(pRecord)
			{
				tempPrepareStringRecord(pRecord);
				if (!pRecord.ClauseDescriptor?.DataType)
				{
					pRecord.ClauseDescriptor.DataType = pRecord.DataType || 'String';
				}
				if (!pRecord.ClauseDescriptor?.PictForm)
				{
					pRecord.ClauseDescriptor.PictForm = pRecord.PictForm || {};
				}
			};
			// The module's add-filter popover owns field visibility now — hand it our blacklist so
			// internal/audit columns (DeleteDate, ExternalSync*, etc.) stay hidden from it.
			this.pict.views['PRSP-Filters'].filterFieldBlacklist = this.blacklistedFilters;
			// Quick Filters are opt-in across Headlight: the curated bar shows only on record sets that
			// declare an explicit `QuickFilters` config (no schema-derived clever defaults). (The recordset
			// date-range filter fix still applies everywhere regardless of this.)
			this.pict.views['PRSP-Filters'].quickFiltersAutoDefault = false;
			this.renderOverriden = true;
		}
		// update the filter state
		this.updateFilters();

		// (The entity SelectedValueList template overrides that used to live here are gone — the module's
		// entity-reference base owns its input template natively now, delegating to the Picker InputType.)
		//@ts-ignore
		this.pict.PictApplication.changeTheme('Edit');
		// The filter clause + chrome + the add-filter popover + the entity-reference inputs are all owned
		// by pict-section-recordset now (entity filters delegate to the pict-native 'Picker' InputType,
		// opted in above) and branded via Headlight's --theme-color-* tokens; this provider just hands the
		// module its field blacklist (above). What remains here is the filter-experience manager (below).
		this.pict.TemplateProvider.addTemplate('FilterPersistenceView-Container', /*html*/`
			<!-- LIMS Custom pict view template: [FilterPersistenceView-Container] -->
			<div id="FilterPersistenceView-Content">
				<!-- Content for Filter Persistence View goes here -->
				<div id="FilterPersistenceView-Body">
					<label class="label" for="FilterPersistenceView-CurrentFilterNameInput">Current Filter Experience: <span id="FilterPersistenceView-CurrentFilterNameInput-ValidationMessage" class="is-size-7 has-text-weight-normal has-text-danger-50 ml-1"></span></label>
					<fieldset class="FilterPersistenceView-ActiveSettings field has-addons has-addons-right">						
						<p class="control is-expanded">
							<input class="input" type="text" id="FilterPersistenceView-CurrentFilterNameInput" name="CurrentFilterName" value="" onfocus="this.select()" />
						</p>
						<p class="control has-text-right">

							<button type="button" class="button is-info" id="FilterPersistenceView-SaveFilterButton" onclick="_Pict.providers.ComplexFilterProvider.handleSaveFilterPersistenceSettings(event, '{~D:Record.RecordSet~}', '{~D:Record.ViewContext~}')">
								<span class="icon"><i class="fas fa-save"></i></span>
								<span id="FilterPersistenceView-SaveFilterButtonText" class="icon-text">Save</span>
							</button>
						</p>
					</fieldset>
					<div class="FilterPersistenceView-StoredSettings">
						<!-- Dropdown for stored filter experiences -->
						<fieldset class="field">
							<label class="label" for="FilterPersistenceView-StoredFiltersSelect">Saved Filter Experiences:</label>
							<p class="control select is-fullwidth">
								<select class="input" id="FilterPersistenceView-StoredFiltersSelect" onchange="_Pict.views['FilterPersistenceView'].setFilterExperienceToSelection(event)" name="StoredFilterName">
									<!-- Options will be populated dynamically -->
								</select>
							</p>
							<p class="control has-text-right mt-2">
								<button type="button" class="button is-info" id="FilterPersistenceView-LoadFilterButton" 
									onclick="_Pict.views['FilterPersistenceView'].loadFilterPersistenceSettings(event);">
									<span class="icon"><i class="fas fa-download"></i></span>
									<span class="icon-text">Load</span>
								</button>
								<button type="button" class="button is-info" id="FilterPersistenceView-SetAsDefaultButton" onclick="_Pict.views['FilterPersistenceView'].toggleFilterExperienceAsTheDefault(event, true)">
									<span class="icon"><i class="fas fa-star"></i></span>
									<span class="icon-text">Set As Default</span>
								</button>
								<button type="button" class="button is-info" id="FilterPersistenceView-RemoveAsDefaultButton" onclick="_Pict.views['FilterPersistenceView'].toggleFilterExperienceAsTheDefault(event, false)">
									<span class="icon"><i class="fas fa-star-half-alt"></i></span>
									<span class="icon-text">Remove As Default</span>
								</button>
								<button type="button" class="button is-info" id="FilterPersistenceView-DeleteFilterButton" onclick="_Pict.providers.ComplexFilterProvider.handleDeleteFilterPersistenceSettings(event)">
									<span class="icon"><i class="fas fa-trash"></i></span>
									<span class="icon-text">Delete</span>
								</button>
							</p>
						</fieldset>
					</div>
				</div>
				<div id="FilterPersistenceView-Footer" class="container pt-3">
					<div class="columns is-vcentered">
						<div class="column">
							<label for="OptionalSettings-RememberLastUsed" class="label">
								<input type="checkbox" id="OptionalSettings-RememberLastUsed" name="RememberLastUsed" onchange="_Pict.views['FilterPersistenceView'].toggleRememberLastUsedFilterExperience(event)"	 />
								Remember my last search filter experience
							</label>
						</div>
					</div>
				</div>
			</div>
			<!-- LIMS Custom end view template:  [FilterPersistenceView-Container] -->
		`);
	}
}

module.exports = ComplexFilterProvider;
module.exports.default_configuration = {};
